import * as React from 'react'
import { cn } from '@/registry/reapit/lib/utils'

// ─── lookup maps (static strings so Tailwind v4 scanner picks them all up) ────

// DS size scale: XS (12px), S (14px), M (16px), L (20px), XL (24px), 2XL (28px), 3XL (36px).
// After Phase 1 the Tailwind overrides map these exactly:
//   text-xs = 12px, text-sm = 14px, text-base = 16px, text-lg = 20px,
//   text-xl = 24px, text-2xl = 28px, text-3xl = 36px.
// Note: '4xl' is retained for escape-hatch use but has no DS named equivalent.
const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
} as const

// DS permits only Regular (400), Medium (500), and Semi Bold (600).
// bold (700) and extrabold (800) have been removed from the DS weight scale.
const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
} as const

const colourClasses = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  secondary: 'text-text-secondary',
  tertiary: 'text-text-tertiary',
  destructive: 'text-destructive',
  accent: 'text-accent-foreground',
  'accent-1': 'text-text-accent-1',
  'accent-2': 'text-text-accent-2',
  success: 'text-text-success',
  pending: 'text-text-pending',
  warning: 'text-text-warning',
  info: 'text-text-info',
  inherit: '',
} as const

const leadingClasses = {
  none: 'leading-none',
  tight: 'leading-tight',
  snug: 'leading-snug',
  normal: 'leading-normal',
  relaxed: 'leading-relaxed',
  loose: 'leading-loose',
  '7': 'leading-7',
} as const

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const

// ─── Text ─────────────────────────────────────────────────────────────────────

export namespace Text {
  export type Size = keyof typeof sizeClasses
  /** DS-permitted weights only: Regular (400), Medium (500), Semi Bold (600). */
  export type Weight = keyof typeof weightClasses
  export type Colour = keyof typeof colourClasses
  export type Leading = keyof typeof leadingClasses
  export type Align = keyof typeof alignClasses

  export interface Props extends React.ComponentProps<'span'> {
    /**
     * Render as a different element.
     * Defaults to 'p' when `block` is true, otherwise 'span'.
     */
    as?: React.ElementType
    /**
     * Font size aligned to the DS type scale.
     * Defaults to 'base' (DS M, 16px) when not provided.
     *
     * | Prop  | DS name | Size  |
     * |-------|---------|-------|
     * | xs    | XS      | 12px  |
     * | sm    | S       | 14px  |
     * | base  | M       | 16px  |
     * | lg    | L       | 20px  |
     * | xl    | XL      | 24px  |
     * | 2xl   | 2XL     | 28px  |
     * | 3xl   | 3XL     | 36px  |
     */
    size?: Size
    /** Font weight. DS-permitted values: 'normal' (400), 'medium' (500), 'semibold' (600). */
    weight?: Weight
    /**
     * Text colour using design-system semantic tokens.
     * 'muted' → text-muted-foreground, 'primary' → text-primary, etc.
     */
    colour?: Colour
    /** line-height. */
    leading?: Leading
    /** text-align. */
    align?: Align
    /** Truncate overflow with an ellipsis. */
    truncate?: boolean
    /** Renders as a block element (p) rather than inline (span). */
    block?: boolean
  }
}

export function Text({
  as,
  size = 'base',
  weight,
  colour,
  leading,
  align,
  truncate = false,
  block = false,
  className,
  children,
  ...props
}: Text.Props) {
  const Comp = as ?? (block ? 'p' : 'span')
  return (
    <Comp
      className={cn(
        sizeClasses[size],
        weight && weightClasses[weight],
        colour && colourClasses[colour],
        leading && leadingClasses[leading],
        align && alignClasses[align],
        truncate && 'truncate',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Convenience composites ───────────────────────────────────────────────────

/** DS S (14px Regular) with secondary colour — equivalent to text-sm text-muted-foreground. */
export function TextMuted({ className, ...props }: Omit<Text.Props, 'size' | 'colour'>) {
  return <Text size="sm" colour="muted" className={className} {...props} />
}

/**
 * Section or card heading — DS XL (24px Semi Bold).
 * Maps to text-xl font-semibold after Phase 1 breakpoint overrides.
 */
export function TextHeading({ className, ...props }: Omit<Text.Props, 'size' | 'weight'>) {
  return <Text size="xl" weight="semibold" block className={className} {...props} />
}

/** DS S Medium (14px 500) — equivalent to text-sm font-medium. */
export function TextLabel({ className, ...props }: Omit<Text.Props, 'size' | 'weight'>) {
  return <Text size="sm" weight="medium" className={className} {...props} />
}

/**
 * Reapit "XS" sub-scale (12px) — for dense metadata, timestamps, and compact UI.
 * This size is below the DS named scale (which starts at S = 14px) but is a
 * permitted Reapit-specific extension. Equivalent to text-xs text-muted-foreground.
 */
export function TextCaption({ className, ...props }: Omit<Text.Props, 'size' | 'colour'>) {
  return <Text size="xs" colour="muted" className={className} {...props} />
}
